<template>
  <Transition name="fade-scale">
    <div 
      v-if="isVisible" 
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 select-none"
    >
      <div class="bg-slate-900 border border-slate-800 shadow-2xl rounded-2xl p-6 sm:p-8 max-w-md w-full text-center relative overflow-hidden group">
        <!-- Background Glowing Gradient Accents -->
        <div class="absolute -top-24 -left-24 w-48 h-48 bg-unibo-red/20 rounded-full blur-3xl group-hover:bg-unibo-red/30 transition-all"></div>
        <div class="absolute -bottom-24 -right-24 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl group-hover:bg-amber-500/30 transition-all"></div>

        <!-- Center Glowing Animated Loader Spinner -->
        <div class="relative w-20 h-20 mx-auto mb-6 flex items-center justify-center">
          <!-- Outer Pulsing Ring -->
          <div class="absolute inset-0 rounded-full border-2 border-dashed border-amber-500/40 animate-spin-slow"></div>
          <!-- Inner Rotating Spinner -->
          <div class="absolute inset-2 rounded-full border-2 border-t-unibo-red border-r-transparent border-b-amber-400 border-l-transparent animate-spin"></div>
          <!-- Center Python Icon -->
          <i class="pi pi-bolt text-2xl text-amber-400 animate-pulse"></i>
        </div>

        <!-- Title Header -->
        <h3 class="text-base font-bold text-slate-100 mb-1 tracking-tight">
          Initializing UNIBO Python ML Engine
        </h3>

        <!-- Status Message Subtitle -->
        <p class="text-xs text-slate-400 mb-6 min-h-[36px] flex items-center justify-center font-mono px-2 leading-relaxed">
          {{ message || 'Loading WebAssembly CPython 3.11 Runtime...' }}
        </p>

        <!-- Progress Bar -->
        <div class="w-full bg-slate-800 rounded-full h-2 mb-4 overflow-hidden p-0.5 border border-slate-700/80 shadow-inner">
          <div 
            class="bg-gradient-to-r from-unibo-red via-amber-500 to-emerald-400 h-full rounded-full transition-all duration-500 relative"
            :style="{ width: progressPercentage + '%' }"
          >
            <div class="absolute inset-0 bg-white/20 animate-pulse"></div>
          </div>
        </div>

        <!-- Packages Pre-loaded Chips -->
        <div class="flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-mono text-slate-400">
          <span
            v-for="pkg in packageChips"
            :key="pkg"
            class="px-2 py-0.5 rounded bg-slate-800 border border-slate-700/60 flex items-center"
          >
            <i class="pi pi-check text-[9px] text-emerald-400 mr-1" v-if="status === 'ready'"></i>
            <i class="pi pi-spin pi-spinner text-[9px] text-amber-400 mr-1" v-else-if="status === 'packages_loading'"></i>
            <i class="pi pi-spin pi-spinner text-[9px] text-amber-400 mr-1" v-else-if="status === 'loading' && pkg === 'NumPy'"></i>
            <i class="pi pi-circle text-[9px] text-slate-600 mr-1" v-else></i>
            {{ pkg }}
          </span>
        </div>

        <!-- Retry Button if error -->
        <button 
          v-if="status === 'error'"
          @click="$emit('retry')"
          class="mt-5 px-4 py-1.5 bg-unibo-red text-white text-xs font-bold rounded-lg hover:bg-unibo-red/90 transition-colors shadow-md flex items-center justify-center space-x-1.5 mx-auto"
        >
          <i class="pi pi-refresh text-xs"></i>
          <span>Retry Loading Engine</span>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { PyodideStatus } from '../types';

const props = defineProps<{
  status: PyodideStatus;
  message: string;
}>();

defineEmits(['retry']);

const packageChips = ['NumPy', 'SciPy', 'Pandas', 'Matplotlib', 'Scikit-Learn', 'Seaborn'] as const;

const isVisible = computed(() => {
  return props.status !== 'ready';
});

const progressPercentage = computed(() => {
  switch (props.status) {
    case 'uninitialized': return 10;
    case 'loading': return 40;
    case 'packages_loading': return 80;
    case 'ready': return 100;
    case 'error': return 100;
    default: return 20;
  }
});
</script>

<style scoped>
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.4s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-spin-slow {
  animation: spin-slow 8s linear infinite;
}
</style>
